/**
 * Profile page — the user's self-service hub.
 *
 * Tabs:
 *   Info         — first/last name, email, organization
 *   Password     — change own password
 *   API keys     — list/create/revoke own keys
 *   Preferences  — language + theme picker; writes to /me/preferences
 *                  AND mirrors locally so UI updates without round-trip
 *
 * Read access for every authenticated user (uses /api/admin/me/*,
 * which doesn't require admin permissions).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Check,
  CheckCircle2,
  Copy,
  KeyRound,
  Loader2,
  Plus,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageContainer, PageContent, PageHeader } from "@/components/ui/page";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TwoFactorPanel } from "@/components/auth/totp";
import { PermissionPicker } from "@/components/auth/PermissionPicker";
import { hasPermission } from "@/lib/permissions";
import { api, ApiError } from "@/lib/api";
import { operatorTotpAdapter } from "@/lib/api-2fa";
import {
  meApi,
  type MyApiKeyCreated,
} from "@/lib/api-me";
import { setLanguage, type SupportedLanguage } from "@/i18n";
import { useTheme } from "@/theme/ThemeProvider";

function InfoTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const profileQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => meApi.get(),
  });

  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [organization, setOrganization] = React.useState("");

  React.useEffect(() => {
    if (profileQuery.data) {
      setFirstName(profileQuery.data.first_name ?? "");
      setLastName(profileQuery.data.last_name ?? "");
      setEmail(profileQuery.data.email);
      setOrganization(profileQuery.data.organization ?? "");
    }
  }, [profileQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      meApi.update({
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
        email: email.trim(),
        organization: organization.trim() || null,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });

  if (!profileQuery.data) {
    return <div className="text-sm text-muted-foreground">{t("common.loading")}</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("profile.tabs.info")}</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveMutation.mutate();
          }}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="first-name">{t("profile.firstName")}</Label>
              <Input
                id="first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last-name">{t("profile.lastName")}</Label>
              <Input
                id="last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="email">{t("common.email")}</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="org">{t("profile.organization")}</Label>
            <Input
              id="org"
              value={organization}
              onChange={(e) => setOrganization(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t("common.username")}</Label>
            <div className="font-mono text-sm text-muted-foreground">
              {profileQuery.data.username}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t("sidebar.users")}</Label>
            <div className="flex flex-wrap gap-1">
              {profileQuery.data.groups.map((g) => (
                <Badge key={g} tone="neutral">
                  {g}
                </Badge>
              ))}
            </div>
          </div>

          {saveMutation.isError && (
            <Alert tone="danger">
              {(saveMutation.error as ApiError)?.message ?? "Save failed"}
            </Alert>
          )}
          {saveMutation.isSuccess && (
            <Alert tone="success">{t("profile.saved")}</Alert>
          )}

          <Button type="submit" disabled={saveMutation.isPending}>
            {saveMutation.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
            )}
            {t("profile.save")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function PasswordTab() {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmPassword, setConfirmPassword] = React.useState("");

  const mutation = useMutation({
    mutationFn: () =>
      api.post<void>("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      }),
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    },
  });

  const passwordsMatch = newPassword === confirmPassword;
  const valid =
    currentPassword.length > 0 &&
    newPassword.length >= 8 &&
    passwordsMatch &&
    newPassword !== currentPassword;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          {t("profile.changePassword")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="max-w-md space-y-3"
        >
          <div className="space-y-1.5">
            <Label htmlFor="cur">{t("profile.currentPassword")}</Label>
            <Input
              id="cur"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new">{t("profile.newPassword")}</Label>
            <Input
              id="new"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">{t("profile.confirmPassword")}</Label>
            <Input
              id="confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            {confirmPassword.length > 0 && !passwordsMatch && (
              <p className="text-xs text-destructive">
                {t("profile.passwordMismatch")}
              </p>
            )}
          </div>

          {mutation.error && (
            <Alert tone="danger">
              {(mutation.error as ApiError).message}
            </Alert>
          )}
          {mutation.isSuccess && (
            <Alert tone="success">
              <div className="flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-success" strokeWidth={1.5} />
                {t("profile.passwordChanged")}
              </div>
            </Alert>
          )}

          <Button type="submit" disabled={!valid || mutation.isPending}>
            {mutation.isPending && (
              <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
            )}
            {t("profile.changePassword")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function ApiKeysTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [creating, setCreating] = React.useState(false);
  const [newName, setNewName] = React.useState("");
  const [newPerms, setNewPerms] = React.useState<string[]>([]);
  const [justCreated, setJustCreated] = React.useState<MyApiKeyCreated | null>(
    null,
  );
  const [copied, setCopied] = React.useState(false);

  const query = useQuery({
    queryKey: ["me-api-keys"],
    queryFn: () => meApi.listApiKeys(),
  });

  // A personal token acts AS the caller, so its permissions must be a SUBSET
  // of the caller's own grants (the backend rejects any that aren't). We read
  // /me once and gate the picker with a wildcard-aware `hasPermission` check so
  // the operator can only tick what they already hold (an admin with `*:*` sees
  // everything, a scoped operator only their grants).
  const me = useQuery({ queryKey: ["me"], queryFn: () => meApi.get() });
  const ownGrants = me.data?.permissions ?? [];

  const createMutation = useMutation({
    mutationFn: () =>
      meApi.createApiKey(newName || undefined, undefined, newPerms),
    onSuccess: (key) => {
      qc.invalidateQueries({ queryKey: ["me-api-keys"] });
      setJustCreated(key);
      setNewName("");
      setNewPerms([]);
      setCreating(false);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => meApi.revokeApiKey(keyId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me-api-keys"] }),
  });

  async function handleCopy(value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          {t("profile.tabs.apiKeys")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {justCreated && (
          <Alert tone="warning">
            <div className="space-y-2">
              <div className="font-medium">{t("profile.apiKeys.secretReveal")}</div>
              <div className="flex items-center gap-2">
                <code className="block flex-1 overflow-x-auto border border-border bg-background px-2 py-1 font-mono text-xs">
                  {justCreated.value}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleCopy(justCreated.value)}
                >
                  {copied ? (
                    <Check className="h-3.5 w-3.5 text-success" strokeWidth={1.5} />
                  ) : (
                    <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                  )}
                </Button>
              </div>
              <button
                type="button"
                onClick={() => setJustCreated(null)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                {t("common.close")}
              </button>
            </div>
          </Alert>
        )}

        {creating ? (
          <div className="space-y-3 rounded-md border border-border/60 bg-muted/30 p-2">
            <div className="space-y-1">
              <Label htmlFor="new-key-name" className="text-xs">
                {t("profile.apiKeys.nameLabel")} ({t("common.optional")})
              </Label>
              <Input
                id="new-key-name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder={t("profile.apiKeys.createDescription") as string}
                className="h-8 text-xs"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                {t("profile.apiKeys.permissionsLabel", "Permessi")}
              </Label>
              <p className="text-[11px] text-muted-foreground">
                {t(
                  "profile.apiKeys.permissionsHint",
                  "Il token agisce a tuo nome: puoi concedere solo permessi che già possiedi.",
                )}
              </p>
              <PermissionPicker
                value={newPerms}
                onChange={setNewPerms}
                isAvailable={(resource, action) =>
                  hasPermission(ownGrants, resource, action)
                }
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending || newPerms.length === 0}
              >
                {createMutation.isPending && (
                  <Loader2
                    className="h-3.5 w-3.5 animate-spin"
                    strokeWidth={1.5}
                  />
                )}
                {t("common.create")}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setCreating(false);
                  setNewName("");
                  setNewPerms([]);
                }}
              >
                {t("common.cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" strokeWidth={1.5} />
            {t("profile.apiKeys.create")}
          </Button>
        )}

        {query.data && query.data.length === 0 ? (
          <div className="border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
            <div className="font-medium">{t("profile.apiKeys.noKeys")}</div>
            <div className="mt-1">{t("profile.apiKeys.createFirst")}</div>
          </div>
        ) : (
          <div className="rounded-lg overflow-hidden border border-border/60">
            {(query.data ?? []).map((k) => (
              <div
                key={k.key_id}
                className="flex items-center justify-between border-b border-border px-3 py-2 text-xs last:border-b-0"
              >
                <div className="min-w-0">
                  <div className="font-medium">{k.name ?? "(unnamed)"}</div>
                  <div className="font-mono text-xs text-muted-foreground">
                    {t("profile.apiKeys.lastUsed")}:{" "}
                    {k.last_used_at
                      ? new Date(k.last_used_at).toLocaleString()
                      : t("profile.apiKeys.never")}
                  </div>
                  {k.expires_at && (
                    <div className="font-mono text-xs text-muted-foreground">
                      {t("profile.apiKeys.expires")}:{" "}
                      {new Date(k.expires_at).toLocaleString()}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {k.revoked_at ? (
                    <Badge tone="neutral">{t("profile.apiKeys.revoked")}</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={revokeMutation.isPending}
                      onClick={() => revokeMutation.mutate(k.key_id)}
                    >
                      <XCircle
                        className="h-3.5 w-3.5 text-destructive"
                        strokeWidth={1.5}
                      />
                      {t("profile.apiKeys.revoke")}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PreferencesTab() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const { theme, setTheme } = useTheme();

  const profileQuery = useQuery({
    queryKey: ["me"],
    queryFn: () => meApi.get(),
  });

  const langMutation = useMutation({
    mutationFn: (lang: SupportedLanguage) =>
      meApi.updatePreferences({ language: lang }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });

  const themeMutation = useMutation({
    mutationFn: (next: "light" | "dark" | "system") =>
      meApi.updatePreferences({ theme: next }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });

  const currentLang = (i18n.language?.slice(0, 2) ?? "en") as SupportedLanguage;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("profile.tabs.preferences")}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>{t("profile.language")}</Label>
          <div className="inline-flex border border-border">
            {(
              [
                ["en", t("profile.languageEn")],
                ["it", t("profile.languageIt")],
              ] as const
            ).map(([code, label]) => (
              <button
                key={code}
                type="button"
                onClick={() => {
                  setLanguage(code);
                  langMutation.mutate(code);
                }}
                className={
                  "px-3 py-1.5 text-xs " +
                  (currentLang === code
                    ? "bg-accent text-accent-foreground"
                    : "bg-surface text-muted-foreground hover:bg-accent/10")
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t("profile.theme")}</Label>
          <div className="inline-flex border border-border">
            {(
              [
                ["light", t("profile.themeLight")],
                ["dark", t("profile.themeDark")],
                ["system", t("profile.themeSystem")],
              ] as const
            ).map(([code, label]) => (
              <button
                key={code}
                type="button"
                onClick={() => {
                  setTheme(code);
                  themeMutation.mutate(code);
                }}
                className={
                  "px-3 py-1.5 text-xs " +
                  (theme === code
                    ? "bg-accent text-accent-foreground"
                    : "bg-surface text-muted-foreground hover:bg-accent/10")
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {profileQuery.data?.preferences && (
          <div className="border-t border-border pt-3 text-xs text-muted-foreground">
            {t("profile.saved")}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SecurityTab() {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          {t("twoFactor.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="max-w-md">
        <TwoFactorPanel adapter={operatorTotpAdapter} />
      </CardContent>
    </Card>
  );
}

export function ProfilePage() {
  const { t } = useTranslation();
  return (
    <PageContainer>
      <PageHeader
        title={t("profile.title")}
        description={t("profile.subtitle")}
      />
      <PageContent>
        <Tabs defaultValue="info">
          <TabsList>
            <TabsTrigger value="info">{t("profile.tabs.info")}</TabsTrigger>
            <TabsTrigger value="password">
              {t("profile.tabs.password")}
            </TabsTrigger>
            <TabsTrigger value="security">
              {t("twoFactor.tab")}
            </TabsTrigger>
            <TabsTrigger value="api-keys">
              {t("profile.tabs.apiKeys")}
            </TabsTrigger>
            <TabsTrigger value="preferences">
              {t("profile.tabs.preferences")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="info" className="pt-4">
            <InfoTab />
          </TabsContent>
          <TabsContent value="password" className="pt-4">
            <PasswordTab />
          </TabsContent>
          <TabsContent value="security" className="pt-4">
            <SecurityTab />
          </TabsContent>
          <TabsContent value="api-keys" className="pt-4">
            <ApiKeysTab />
          </TabsContent>
          <TabsContent value="preferences" className="pt-4">
            <PreferencesTab />
          </TabsContent>
        </Tabs>
      </PageContent>
    </PageContainer>
  );
}

/** Alias so the existing `/account` route keeps working. */
export const AccountPage = ProfilePage;
